// src/enums.ts
var TASK_STATUSES = [
  "inbox",
  "next",
  "scheduled",
  "waiting",
  "delegated",
  "blocked",
  "someday",
  "done"
];
var TASK_PRIORITIES = ["critical", "important", "routine", "low"];
var PRIORITY_RANK = Object.fromEntries(
  TASK_PRIORITIES.map((p, i) => [p, i])
);
var STATUS_RANK = Object.fromEntries(
  TASK_STATUSES.map((s, i) => [s, i])
);
function taskPriorityRank(p) {
  return PRIORITY_RANK[p];
}
function taskStatusRank(s) {
  return STATUS_RANK[s];
}
var SOURCE_KINDS = [
  "manual",
  "email",
  "slack",
  "meeting",
  "mobile_capture",
  "calendar",
  "phone",
  "other"
];
var PROJECT_STATUSES = ["on_track", "needs_attention", "idle", "blocked"];
var ARCHIVE_REASONS = ["completed", "dropped", "paused", "replaced"];
var WORKSPACE_ROLES = ["owner", "member", "viewer"];
var APPROVAL_STATUSES = ["pending", "approved", "rejected"];
var PLATFORM_ROLES = ["admin"];
var PLATFORM_ADMIN_EMAILS = ["joao@jrc.pt"];
function isPlatformAdminEmail(email) {
  const lower = email.toLowerCase();
  return PLATFORM_ADMIN_EMAILS.some((e) => e.toLowerCase() === lower);
}
var ACTOR_KINDS = ["user", "agent", "system"];
var VERIFICATION_PURPOSES = ["magic_link", "email_verify", "password_reset"];
var DEFAULT_CONTEXTS = [
  { slug: "personal", label: "Personal", color: "#7a5a8a", sortOrder: 1 },
  { slug: "professional", label: "Professional", color: "#4a6b8a", sortOrder: 2 }
];
var FINANCE_ACCOUNT_TYPES = ["checking", "savings", "credit-card", "investment", "loan", "cash"];
var FINANCE_ACCOUNT_GROUPS = ["asset", "liability"];
var FINANCE_CATEGORY_KINDS = ["income", "expense"];
var FINANCE_TRANSFER_STATUSES = ["suggested", "confirmed"];
var FINANCE_MATCHED_BY = ["rule", "agent", "user", "migration"];
var FINANCE_CATEGORIZED_BY = ["rule", "agent", "user", "migration"];
var FINANCE_RULE_MATCH_FIELDS = ["description", "bank_category", "bank_type"];
var FINANCE_RULE_MATCH_TYPES = ["contains", "equals"];

// src/finance-rules.ts
function ruleCriteriaCount(c) {
  return (c.conditions?.length ?? 0) + (c.amountMin != null ? 1 : 0) + (c.amountMax != null ? 1 : 0) + (c.accountId ? 1 : 0);
}
function ruleActionCount(a) {
  return (a.categoryId ? 1 : 0) + (a.move ? 1 : 0);
}

// src/apps.ts
var APP_SLUGS = ["tasks", "health", "finance"];
var PLATFORM_APP_STATUSES = ["active", "planned"];
var WORKSPACE_APP_STATUSES = ["enabled", "disabled"];
var PLATFORM_APPS = {
  tasks: {
    slug: "tasks",
    name: "K-OS Tasks",
    description: "Task, project, area, inbox, and follow-up management.",
    status: "active",
    routeBase: "/tasks",
    apiBase: "/api"
  },
  health: {
    slug: "health",
    name: "K-OS Health",
    description: "Health, training, recovery, and wellbeing management.",
    status: "active",
    routeBase: "/health",
    apiBase: "/api/apps/health"
  },
  finance: {
    slug: "finance",
    name: "K-OS Finance",
    description: "Family finance: accounts, transactions, transfers, budgets, and reports.",
    status: "active",
    routeBase: "/finance",
    apiBase: "/api/apps/finance"
  }
};
var DEFAULT_WORKSPACE_APP_SLUGS = ["tasks"];
function isAppSlug(value) {
  return APP_SLUGS.includes(value);
}
var LANDING_APPS = ["platform", ...APP_SLUGS];
function isLandingApp(value) {
  return LANDING_APPS.includes(value);
}
function landingRouteFor(value) {
  if (!value || !isLandingApp(value)) return "/apps";
  if (value === "platform") return "/apps";
  return PLATFORM_APPS[value].routeBase;
}

// src/audit.ts
var AUDITED_TASK_FIELDS = [
  "due_at",
  "scheduled_at",
  "review_at",
  "project_id",
  "area_id",
  "person_id",
  "owner_id",
  "owner_agent_id",
  "context_id",
  "description"
];
var STRUCTURAL_TASK_EVENT_KINDS = [
  "created",
  "status_changed",
  "priority_changed",
  "completed",
  "archived",
  "restored",
  "commented",
  "agent_suggested",
  "agent_acted"
];
function diffAuditedFields(previous, next) {
  const changes = [];
  for (const field of AUDITED_TASK_FIELDS) {
    if (previous[field] !== next[field]) {
      changes.push({ field, from: previous[field], to: next[field] });
    }
  }
  return changes;
}

// src/recurring.ts
var WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
var WEEKDAY_INDEX = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 0
};
function nextOccurrences(rule, from, until) {
  if (rule.interval <= 0) return [];
  const out = [];
  const start = startOfDay(from);
  const end = startOfDay(until);
  switch (rule.kind) {
    case "daily": {
      let d = start;
      while (d.getTime() <= end.getTime()) {
        out.push(d);
        d = addDays(d, rule.interval);
      }
      break;
    }
    case "weekly": {
      const wanted = new Set(rule.weekdays.map((w) => WEEKDAY_INDEX[w]));
      const startWeek = startOfWeek(start);
      let d = start;
      while (d.getTime() <= end.getTime()) {
        const weeksSinceStart = Math.floor(
          (startOfWeek(d).getTime() - startWeek.getTime()) / (7 * 864e5)
        );
        if (weeksSinceStart % rule.interval === 0 && wanted.has(d.getDay())) {
          out.push(d);
        }
        d = addDays(d, 1);
      }
      break;
    }
    case "monthly_day": {
      const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      let m = startMonth;
      while (m.getTime() <= end.getTime()) {
        const day = rule.dayOfMonth;
        const candidate = new Date(m.getFullYear(), m.getMonth(), day);
        if (candidate.getMonth() === m.getMonth()) {
          if (candidate.getTime() >= start.getTime() && candidate.getTime() <= end.getTime()) {
            out.push(candidate);
          }
        }
        m = addMonths(m, rule.interval);
      }
      break;
    }
    case "monthly_weekday": {
      const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      const wd = WEEKDAY_INDEX[rule.weekday];
      let m = startMonth;
      while (m.getTime() <= end.getTime()) {
        const candidate = nthWeekdayOfMonth(m, wd, rule.week);
        if (candidate && candidate.getTime() >= start.getTime() && candidate.getTime() <= end.getTime()) {
          out.push(candidate);
        }
        m = addMonths(m, rule.interval);
      }
      break;
    }
  }
  return out;
}
function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}
function addDays(d, n) {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}
function addMonths(d, n) {
  const c = new Date(d);
  c.setMonth(c.getMonth() + n);
  return c;
}
function startOfWeek(d) {
  const c = startOfDay(d);
  const dow = c.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  c.setDate(c.getDate() + offset);
  return c;
}
function nthWeekdayOfMonth(month, weekday, n) {
  if (n === -1) {
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    let d2 = last;
    while (d2.getDay() !== weekday) d2 = addDays(d2, -1);
    return d2;
  }
  let d = new Date(month.getFullYear(), month.getMonth(), 1);
  let count = 0;
  while (d.getMonth() === month.getMonth()) {
    if (d.getDay() === weekday) {
      count++;
      if (count === n) return d;
    }
    d = addDays(d, 1);
  }
  return null;
}

// src/navigation.ts
var NAV_GROUPS = [
  {
    group: "Workspace",
    items: [
      { id: "inbox", name: "Inbox", path: "/tasks/inbox", icon: "inboxAlt" },
      { id: "today", name: "Today", path: "/tasks", icon: "today" },
      {
        id: "suggestions",
        name: "Suggestions",
        path: "/tasks/suggestions",
        icon: "sparkles"
      },
      { id: "upcoming", name: "Upcoming", path: "/tasks/upcoming", icon: "upcoming" },
      { id: "waiting", name: "Waiting", path: "/tasks/waiting", icon: "waiting" },
      { id: "all", name: "All tasks", path: "/tasks/all", icon: "list" },
      { id: "calendar", name: "Calendar", path: "/tasks/calendar", icon: "calendar" },
      { id: "review", name: "Review", path: "/tasks/review", icon: "review" }
    ]
  },
  {
    group: "Organize",
    items: [
      { id: "projects", name: "Projects", path: "/tasks/projects", icon: "projects" },
      { id: "areas", name: "Areas", path: "/tasks/areas", icon: "areas" },
      { id: "people", name: "People", path: "/tasks/people", icon: "people" }
    ]
  },
  {
    group: "Focus",
    items: [
      { id: "pomodoro", name: "Pomodoro Timer", path: "/tasks/pomodoro", icon: "timer" }
    ]
  }
];
var HEALTH_NAV_GROUPS = [
  {
    group: "Today",
    items: [
      { id: "health-today", name: "Today", path: "/health", icon: "today" },
      { id: "health-checkin", name: "Check-in", path: "/health/checkin", icon: "inboxAlt" },
      { id: "health-workouts", name: "Workouts", path: "/health/workouts", icon: "upcoming" },
      { id: "health-exercises", name: "Exercises", path: "/health/exercises", icon: "list" }
    ]
  },
  {
    group: "Track",
    items: [
      {
        id: "health-measurements",
        name: "Measurements",
        path: "/health/measurements",
        icon: "list"
      },
      { id: "health-nutrition", name: "Nutrition", path: "/health/nutrition", icon: "review" },
      { id: "health-sleep", name: "Sleep", path: "/health/sleep", icon: "waiting" }
    ]
  },
  {
    group: "Plan",
    items: [
      { id: "health-goals", name: "Goals", path: "/health/goals", icon: "projects" },
      { id: "health-phases", name: "Phases", path: "/health/phases", icon: "areas" },
      { id: "health-reports", name: "Reports", path: "/health/reports", icon: "sparkles" }
    ]
  },
  {
    group: "Profile",
    items: [
      { id: "health-profile", name: "Profile", path: "/health/profile", icon: "people" },
      {
        id: "health-data-loads",
        name: "Data loads",
        path: "/health/data-loads",
        icon: "inboxAlt"
      }
    ]
  }
];
var FINANCE_NAV_GROUPS = [
  {
    group: "Overview",
    items: [
      { id: "finance-dashboard", name: "Dashboard", path: "/finance", icon: "today" },
      { id: "finance-review", name: "Review", path: "/finance/review", icon: "review" }
    ]
  },
  {
    group: "Money",
    items: [
      {
        id: "finance-transactions",
        name: "Transactions",
        path: "/finance/transactions",
        icon: "list"
      },
      { id: "finance-import", name: "Import", path: "/finance/import", icon: "inboxAlt" },
      { id: "finance-transfers", name: "Transfers", path: "/finance/transfers", icon: "link" }
    ]
  },
  {
    group: "Structure",
    items: [
      { id: "finance-accounts", name: "Accounts", path: "/finance/accounts", icon: "areas" },
      {
        id: "finance-categories",
        name: "Categories",
        path: "/finance/categories",
        icon: "projects"
      },
      { id: "finance-rules", name: "Rules", path: "/finance/rules", icon: "filter" },
      { id: "finance-budgets", name: "Budgets", path: "/finance/budgets", icon: "flag" }
    ]
  },
  {
    group: "Insight",
    items: [
      { id: "finance-reports", name: "Reports", path: "/finance/reports", icon: "sparkles" },
      { id: "finance-settings", name: "Settings", path: "/finance/settings", icon: "settings" }
    ]
  }
];
export {
  ACTOR_KINDS,
  APPROVAL_STATUSES,
  APP_SLUGS,
  ARCHIVE_REASONS,
  AUDITED_TASK_FIELDS,
  DEFAULT_CONTEXTS,
  DEFAULT_WORKSPACE_APP_SLUGS,
  FINANCE_ACCOUNT_GROUPS,
  FINANCE_ACCOUNT_TYPES,
  FINANCE_CATEGORIZED_BY,
  FINANCE_CATEGORY_KINDS,
  FINANCE_MATCHED_BY,
  FINANCE_NAV_GROUPS,
  FINANCE_RULE_MATCH_FIELDS,
  FINANCE_RULE_MATCH_TYPES,
  FINANCE_TRANSFER_STATUSES,
  HEALTH_NAV_GROUPS,
  LANDING_APPS,
  NAV_GROUPS,
  PLATFORM_ADMIN_EMAILS,
  PLATFORM_APPS,
  PLATFORM_APP_STATUSES,
  PLATFORM_ROLES,
  PROJECT_STATUSES,
  SOURCE_KINDS,
  STRUCTURAL_TASK_EVENT_KINDS,
  TASK_PRIORITIES,
  TASK_STATUSES,
  VERIFICATION_PURPOSES,
  WEEKDAYS,
  WORKSPACE_APP_STATUSES,
  WORKSPACE_ROLES,
  diffAuditedFields,
  isAppSlug,
  isLandingApp,
  isPlatformAdminEmail,
  landingRouteFor,
  nextOccurrences,
  ruleActionCount,
  ruleCriteriaCount,
  taskPriorityRank,
  taskStatusRank
};
